import React, { useState, useRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Formik, Form, Field } from 'formik';
import Swal from 'sweetalert2';

const MODAL_STYLES = {
  container: {
    backgroundColor: '#ffffff',
    padding: '15px',
    minWidth: '900px'
  },
  tableWrapper: {
    maxHeight: '500px',
    overflow: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#4a5568 #e2e8f0'
  }
};

const TRANSFORMATIONS = {
  upgrade_https: 'Upgrade to HTTPS',
  resolve_redirects: 'Resolve Redirects',
  localize_china: 'Localize for China'
};

// Component types that contain dynamic links (not editable in editor)
const DYNAMIC_COMPONENTS = [
  'FeedAggregator',
  'FacetedSearch',
  'Block'
];

function getAllComponents(model, result = []) {
  result.push(model);
  model.components().each(mod => getAllComponents(mod, result));
  return result;
}

function findComponentByEl(editor, el) {
  const components = getAllComponents(editor.getWrapper());
  return components.find(comp => comp.getEl && comp.getEl() === el) || null;
}

function isValidUrl(string) {
  const trimmed = string.trim();
  if (trimmed.startsWith('/')) return true;
  if (trimmed.startsWith('tel:') || trimmed.startsWith('mailto:')) return true;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

// Keeps each verify_links / transform_links call well under the ~30s CDN read
// timeout (~0.5-1s per URL observed). Matches the server-side per-request cap
// in app/admin/pages.rb; bump both together.
const BATCH_SIZE = 25;

async function postUrlsInBatches(endpoint, urls, { onProgress, signal } = {}) {
  const results = [];
  const total = urls.length;
  if (total === 0) return { ok: true, results, completed: 0 };

  for (let start = 0; start < urls.length; start += BATCH_SIZE) {
    if (signal?.aborted) return { ok: false, aborted: true, results, completed: start };
    const batch = urls.slice(start, start + BATCH_SIZE);
    let response;
    try {
      response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({ urls: batch }),
        credentials: 'same-origin',
        signal
      });
    } catch (error) {
      if (error.name === 'AbortError') return { ok: false, aborted: true, results, completed: start };
      console.error('Page links fetch failed', { endpoint, batchStart: start, error });
      return { ok: false, results, completed: start, error };
    }
    if (!response.ok) {
      console.error('Page links fetch failed', { endpoint, batchStart: start, status: response.status });
      return { ok: false, response, results, completed: start };
    }
    const data = await response.json();
    if (!Array.isArray(data?.results) || data.results.length !== batch.length) {
      console.error('Page links batch returned malformed results', { endpoint, batchStart: start, expected: batch.length, received: data?.results?.length });
      return { ok: false, results, completed: start, error: new Error('malformed_batch') };
    }
    results.push(...data.results);
    onProgress?.(start + batch.length, total);
  }
  return { ok: true, results, completed: total };
}

function serverErrorMessage(outcome, total) {
  const completed = outcome.completed ?? 0;
  if (completed > 0 && completed < total) {
    return `Server error after processing ${completed} of ${total} links. Please try again.`;
  }
  return 'Server error. Please try again.';
}

const PageLinksTable = ({ links: initialLinks, editor }) => {
  const [links, setLinks] = useState(initialLinks);
  const [linkStatuses, setLinkStatuses] = useState({});
  const [recentlyUpdatedIndices, setRecentlyUpdatedIndices] = useState(new Set());
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [newUrls, setNewUrls] = useState({});
  const [transformation, setTransformation] = useState('');
  const [isApplyingTransformation, setIsApplyingTransformation] = useState(false);
  const [isUpdatingLinks, setIsUpdatingLinks] = useState(false);
  const [alertState, setAlertState] = useState(null);
  const [progressState, setProgressState] = useState(null);
  const abortControllerRef = useRef(null);

  // Abort any in-flight batched request when the modal unmounts.
  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const showAlert = (message, type = 'error') => {
    setAlertState({ message, type });
  };

  const dismissAlert = () => setAlertState(null);

  const showBatchProgress = (done, total) => {
    // Only show the inline progress region when there is more than one batch.
    if (total > BATCH_SIZE) setProgressState({ done, total });
  };

  const runInBatches = (endpoint, urls) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setProgressState(null);
    return postUrlsInBatches(endpoint, urls, {
      onProgress: showBatchProgress,
      signal: controller.signal
    }).finally(() => {
      setProgressState(null);
      if (abortControllerRef.current === controller) abortControllerRef.current = null;
    });
  };

  const selectAll = (setFieldValue, checked) => {
    const value = checked ? links.map((link, idx) => (link.source === 'dynamic' ? null : String(idx))).filter(Boolean) : [];
    setFieldValue('selectedLinks', value);
  };

  const applyTransformationToUrl = (url, transformType) => {
    if (!transformType) return url;
    if (transformType === 'upgrade_https') {
      if (url.startsWith('http://')) {
        return url.replace('http://', 'https://');
      }
      return url;
    }
    return url;
  };

  const applyTransformation = async (selectedIndices) => {
    if (selectedIndices.length === 0) {
      showAlert('Please select at least one link.');
      return;
    }

    setIsApplyingTransformation(true);

    try {
      if (transformation === 'localize_china') {
        const urlsToTranslate = selectedIndices.map(idx => links[Number(idx)].url);
        const outcome = await runInBatches(window.transformLinksURL, urlsToTranslate);
        if (outcome.aborted) return;
        if (!outcome.ok) {
          showAlert(serverErrorMessage(outcome, urlsToTranslate.length));
          return;
        }
        const updates = {};
        selectedIndices.forEach((idxStr, i) => {
          const result = outcome.results[i];
          const idx = Number(idxStr);
          if (result?.changed) updates[idx] = result.transformed_url || links[idx].url;
        });
        setNewUrls(prev => ({ ...prev, ...updates }));
        const updatedIndices = Object.keys(updates).map(Number);
        setRecentlyUpdatedIndices(new Set(updatedIndices));
        showAlert(updatedIndices.length > 0 ? `${updatedIndices.length} of ${selectedIndices.length} links transformed.` : 'No links were changed.', updatedIndices.length > 0 ? 'success' : 'warning');
      } else if (transformation === 'resolve_redirects') {
        const urlsToResolve = selectedIndices.map(idx => links[Number(idx)].url);
        const outcome = await runInBatches(window.verifyLinksURL, urlsToResolve);
        if (outcome.aborted) return;
        if (!outcome.ok) {
          showAlert(serverErrorMessage(outcome, urlsToResolve.length));
          return;
        }
        const redirectCodes = [301, 308]; // Only permanent redirects
        const updates = {};
        selectedIndices.forEach((idxStr, i) => {
          const result = outcome.results[i];
          const idx = Number(idxStr);
          const finalUrl = result?.final_url;
          const status = result?.status;
          if (
              finalUrl &&
              typeof status === 'number' &&
              redirectCodes.includes(status)
          ) {
            updates[idx] = finalUrl;
          }
        });
        setNewUrls(prev => ({ ...prev, ...updates }));
        const updatedIndices = Object.keys(updates).map(Number);
        setRecentlyUpdatedIndices(new Set(updatedIndices));
        showAlert(
            updatedIndices.length > 0
                ? `${updatedIndices.length} of ${selectedIndices.length} links resolved.`
                : 'No links were changed.',
            updatedIndices.length > 0 ? 'success' : 'warning'
        );
      }
      else {
        const updates = {};
        selectedIndices.forEach(idxStr => {
          const idx = Number(idxStr);
          const link = links[idx];
          if (link.source !== 'dynamic') {
            const transformed = applyTransformationToUrl(link.url, transformation);
            if (transformed !== link.url) {
              updates[idx] = transformed;
            }
          }
        });
        setNewUrls(prev => ({ ...prev, ...updates }));
        const updatedIndices = Object.keys(updates).map(Number);
        setRecentlyUpdatedIndices(new Set(updatedIndices));
        showAlert(updatedIndices.length > 0 ? `${updatedIndices.length} of ${selectedIndices.length} links transformed.` : 'No links were changed.', updatedIndices.length > 0 ? 'success' : 'warning');
      }
    } catch (error) {
      console.error('Error applying transformation:', error);
      showAlert('Transformation failed. Please try again.');
    } finally {
      setIsApplyingTransformation(false);
    }
  };

  const checkLinks = async () => {
    setIsCheckingStatus(true);
    const loadingStates = {};
    links.forEach((_, idx) => { loadingStates[idx] = { loading: true }; });
    setLinkStatuses(loadingStates);

    try {
      const urls = links.map(link => link.url);
      const outcome = await runInBatches(window.verifyLinksURL, urls);

      if (outcome.aborted) return;
      if (!outcome.ok) {
        showAlert(serverErrorMessage(outcome, urls.length));
        setLinkStatuses({});
        return;
      }
      const newStatuses = {};
      outcome.results.forEach((result, idx) => {
        newStatuses[idx] = {
          status: result.status,
          loading: false,
          error: result.error
        };
      });
      setLinkStatuses(newStatuses);
    } catch (error) {
      console.error('Error checking links:', error);
      showAlert('Failed to check links. Please try again.');
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const getUpdatableIndices = () => {
    return Object.keys(newUrls)
      .map(Number)
      .filter(idx => (newUrls[idx] || '').trim() && links[idx] && links[idx].source !== 'dynamic');
  };

  const updateLinks = async () => {
    const toUpdate = getUpdatableIndices();

    if (toUpdate.length === 0) {
      showAlert('Enter a new URL for at least one link to update.');
      return;
    }

    const invalidFormat = [];
    const nonHttps = [];
    const invalidStatus = [];

    for (const idx of toUpdate) {
      const newUrl = newUrls[idx].trim();
      if (!isValidUrl(newUrl)) {
        invalidFormat.push({ idx, url: newUrl });
        continue;
      }
      if (newUrl.startsWith('http://')) {
        nonHttps.push({ idx, url: newUrl });
      }
    }

    if (invalidFormat.length > 0) {
      showAlert(`Invalid URL format: ${invalidFormat.map(({ url }) => url).join(', ')}.`);
      return;
    }

    if (nonHttps.length > 0) {
      showAlert('All URLs must use HTTPS.');
      return;
    }

    setIsUpdatingLinks(true);

    try {
      const urlsToCheck = toUpdate.map(idx => newUrls[idx].trim());
      const outcome = await runInBatches(window.verifyLinksURL, urlsToCheck);

      if (outcome.aborted) return;
      if (!outcome.ok) {
        showAlert(serverErrorMessage(outcome, urlsToCheck.length));
        return;
      }
      outcome.results.forEach((result, i) => {
        const idx = toUpdate[i];
        const status = result.status;
        if (typeof status === 'number' && (status < 200 || status >= 400)) {
          invalidStatus.push({
            idx,
            url: newUrls[idx],
            status: status || result.error
          });
        }
      });

      const commitUpdates = () => {
        let updated = 0;
        toUpdate.forEach(idx => {
          const link = links[idx];
          const component = link.anchor && editor ? findComponentByEl(editor, link.anchor) : null;
          if (component) {
            component.addAttributes({ href: newUrls[idx].trim() });
            updated++;
          }
        });

        if (updated > 0) {
          editor.store();
          const refreshedLinks = extractLinksFromCanvas(editor);
          setLinks(refreshedLinks);
          setLinkStatuses({});
          setRecentlyUpdatedIndices(new Set(toUpdate));
        }
        setNewUrls(prev => {
          const next = { ...prev };
          toUpdate.forEach(idx => { delete next[idx]; });
          return next;
        });
        showAlert(`${updated} of ${toUpdate.length} links updated.`, 'success');
      };

      if (invalidStatus.length > 0) {
        const result = await Swal.fire({
          title: 'Non-2xx/3xx responses detected',
          html: `<p>The following links may be unpublished:</p><ul style="text-align:left">${invalidStatus.map(({ url, status }) => `<li><strong>${status}</strong> — ${url}</li>`).join('')}</ul>`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Proceed Anyway',
          cancelButtonText: 'Cancel'
        });
        if (!result.isConfirmed) return;
      }

      commitUpdates();
    } catch (error) {
      console.error('Error updating links:', error);
      showAlert('Update failed. Please try again.');
    } finally {
      setIsUpdatingLinks(false);
    }
  };

  const getStatusBadge = (statusInfo) => {
    if (!statusInfo) return null;
    if (statusInfo.loading) {
      return <i className="fa fa-circle-o-notch fa-spin fa-fw" aria-hidden="true" />;
    }
    const status = statusInfo.status;
    if (status === 'error') {
      return (
        <span className="text-danger" title={statusInfo.error || 'Unknown error'}>
          Error
        </span>
      );
    }
    if (status >= 200 && status < 300) return <span className="text-success" title="OK">{status}</span>;
    if (status >= 300 && status < 400) return <span className="text-warning" title="Redirect">{status}</span>;
    return <span className="text-danger" title={statusInfo.error || `HTTP ${status}`}>{status}</span>;
  };

  const alertTypeClass = alertState?.type === 'success' ? 'nv-alert--success' : alertState?.type === 'warning' ? 'nv-alert--warning' : 'nv-alert--error';

  return (
    <div className="page-links-container" style={MODAL_STYLES.container}>
      <Formik initialValues={{ selectedLinks: [] }}>
        {({ values, setFieldValue }) => (
          <Form>
            {alertState && (
              <div className={`alert alert-dismissible nv-alert nv-alert--with-icon ${alertTypeClass} mb-3`} role="alert">
                <div className="nv-alert__icon" />
                <div className="nv-alert__content">{alertState.message}</div>
                <button type="button" className="btn-close" onClick={dismissAlert} aria-label="Close" />
              </div>
            )}
            {progressState && (
              <div className="page-links-progress form-text text-body-secondary mb-3" role="status" aria-live="polite">
                <i className="fa fa-circle-o-notch fa-spin fa-fw me-2" aria-hidden="true" />
                {I18n.t('admin.page_builder.page_links.batch_progress', { done: progressState.done, total: progressState.total })}
              </div>
            )}
            <div className="mb-3 d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={transformation}
                  onChange={e => setTransformation(e.target.value)}
                >
                  <option value="" disabled>{I18n.t('admin.page_builder.page_links.actions_placeholder')}</option>
                  <option value="upgrade_https">{TRANSFORMATIONS.upgrade_https}</option>
                  <option value="resolve_redirects">{TRANSFORMATIONS.resolve_redirects}</option>
                  <option value="localize_china">{TRANSFORMATIONS.localize_china}</option>
                </select>
                <button
                  type="button"
                  className="nv-cta-primary-alt nv-cta-primary-alt--small"
                  onClick={() => applyTransformation(values.selectedLinks)}
                  disabled={isApplyingTransformation || values.selectedLinks.length === 0 || !transformation}
                >
                  {isApplyingTransformation ? I18n.t('admin.page_builder.page_links.applying') : I18n.t('admin.page_builder.page_links.apply_transformation')}
                </button>
              </div>
            </div>
            {transformation === 'upgrade_https' && (
              <p className="page-links-legend form-text text-body-secondary mb-3">
                {I18n.t('admin.page_builder.page_links.upgrade_https_legend')}
              </p>
            )}
            {transformation === 'resolve_redirects' && (
              <p className="page-links-legend form-text text-body-secondary mb-3">
                {I18n.t('admin.page_builder.page_links.resolve_redirects_legend')}
              </p>
            )}
            {transformation === 'localize_china' && (
              <p className="page-links-legend form-text text-body-secondary mb-3">
                {I18n.t('admin.page_builder.page_links.localize_china_legend')}{' '}
                <a href="https://docs.google.com/document/d/1nj9Oid5SBOFnhZu-8cXs8ukGdV4vDgEZJzin-k8vmV4/edit?usp=sharing" target="_blank" rel="noopener noreferrer">{I18n.t('admin.page_builder.page_links.learn_more')}</a>
              </p>
            )}
            <div style={MODAL_STYLES.tableWrapper} className="nv-table nv-table-alt-row-1 nv-table--p-1">
              <table className="table table-sm mb-0">
                <thead className="table-head">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={e => selectAll(setFieldValue, e.target.checked)}
                        title={I18n.t('admin.page_builder.page_links.select_all')}
                      />
                    </th>
                    <th>{I18n.t('admin.page_builder.page_links.status')}</th>
                    <th className="page-links-url-cell">{I18n.t('admin.page_builder.page_links.url')}</th>
                    <th className="page-links-new-url-cell">{I18n.t('admin.page_builder.page_links.new_url')}</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {links.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center">{I18n.t('admin.page_builder.page_links.no_links')}</td>
                    </tr>
                  ) : (
                    links.map((link, idx) => (
                      <tr key={idx} className={`${recentlyUpdatedIndices.has(idx) ? 'page-links-row--updated' : ''} ${link.source === 'dynamic' ? 'page-links-row--dynamic' : ''}`.trim()}>
                        <td>
                          <Field
                            type="checkbox"
                            name="selectedLinks"
                            value={String(idx)}
                            disabled={link.source === 'dynamic'}
                          />
                        </td>
                        <td>{getStatusBadge(linkStatuses[idx])}</td>
                        <td className="page-links-url-cell">
                          <a href={link.url} target="_blank" rel="noopener noreferrer">
                            {link.url}
                          </a>
                          {link.source === 'dynamic' && (
                            <i className="fa fa-lock ms-2"></i>
                          )}
                        </td>
                        <td className="page-links-new-url-cell">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="https://example.com"
                            value={link.source === 'dynamic' ? '' : (newUrls[idx] || '')}
                            onChange={e => link.source !== 'dynamic' && setNewUrls(prev => ({ ...prev, [idx]: e.target.value }))}
                            disabled={link.source === 'dynamic'}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-3 d-flex justify-content-between">
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="nv-cta-primary-alt nv-cta-primary-alt--small"
                  onClick={checkLinks}
                  disabled={isCheckingStatus || links.length === 0}
                >
                  {isCheckingStatus ? I18n.t('admin.page_builder.page_links.checking') : I18n.t('admin.page_builder.page_links.check_status')}
                </button>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="nv-cta-primary nv-cta-primary--small"
                  onClick={() => updateLinks()}
                  disabled={isUpdatingLinks || getUpdatableIndices().length === 0}
                >
                  {isUpdatingLinks ? I18n.t('admin.page_builder.page_links.updating') : I18n.t('admin.page_builder.page_links.update_links', { count: getUpdatableIndices().length })}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

function extractLinksFromCanvas(editor) {
  const doc = editor.Canvas.getDocument();
  if (!doc) return [];

  const anchorTags = doc.querySelectorAll('a[href]');
  return Array.from(anchorTags)
    .map(anchor => {
      const url = anchor.getAttribute('href');
      const text = anchor.textContent.trim() || url;
      const isDynamic = isInsideReactComponent(anchor);
      return { url, text, source: isDynamic ? 'dynamic' : 'static', anchor };
    })
    .filter(link => link.url);
}

function isInsideReactComponent(element) {
  let current = element;
  while (current && current !== document.body) {
    const gjsType = current.getAttribute('data-gjs-type');
    if (gjsType && DYNAMIC_COMPONENTS.includes(gjsType)) return true;
    current = current.parentElement;
  }
  return false;
}

export function openPageLinksModal(editor) {
  const links = extractLinksFromCanvas(editor);

  const content = document.createElement('div');
  content.id = 'page-links-container';

  editor.Modal.open({ title: I18n.t('admin.page_builder.page_links.title'), content, attributes: { class: 'page-links-modal' } });

  const root = createRoot(content);

  editor.Modal.getModel().once('change:open', () => {
    root.unmount();
  });

  root.render(<PageLinksTable links={links} editor={editor} />);
}
